/**
 * Trial radio grid — radios currently in, or having completed, a billing
 * trial period. Scoped to radios with both a trial start and end date
 * recorded; an optional customer or site-group prop narrows that further to
 * one billing account's radios (AccountTrialUnitsView, which hides the
 * now-redundant customer filter control). With neither prop the
 * caller sees every trial radio the signed-in user's tenant scope allows,
 * filterable by customer, site group, and trial start/end date range.
 *
 * An optional `statusFilter` (a TRIAL_STATUS value, owned by the standalone
 * Trial Units dashboard's stat cards) narrows the grid to that status via
 * the same date-range filter params the dashboard's cards are built from.
 *
 * Server-backed via EntityGrid over odl-app/radios/ (the same descriptor
 * RadioGrid renders), searchable by ICCID/MAC. Deleted (decommissioned)
 * radios are excluded, matching RadioGrid's default-active scope. Customer,
 * Location, Trial Start/End, and Status are FE-curated columns the
 * trial-radios view needs that the descriptor's default radios column set
 * doesn't surface on its own (billing_trial_start_date/end_date ship
 * payload-only; Customer and Status have no descriptor column at all) —
 * added via extraColumns. Trial Start/End and Location are sortable on the
 * descriptor's own SortFields (billing_trial_start_date /
 * billing_trial_end_date / site_name); the trial dates are staff-only
 * fields, matching this page's staff-only scope.
 *
 * Contains:
 * - TrialRadioTable — default export
 */

import { Button, Tooltip } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { listRadios } from '@sophocles/api/radios';
import { useUser } from '@sophocles/app';
import CustomerAutocomplete from '@sophocles/components/CustomerAutocomplete';
import SiteGroupAutocomplete from '@sophocles/components/SiteGroupAutocomplete';
import StatusChip from '@sophocles/components/StatusChip';
import { ClearAllChip, EntityGrid } from '@sophocles/data-grids';
import useColumnVisibilityModel from '@sophocles/data-grids/datasource/useColumnVisibilityModel';
import { formatDate } from '@sophocles/utils/formatUtils';
import {
  DATE_PARAM_FORMAT,
  deriveTrialStatus,
  trialStatusDateFilter,
  TRIAL_SCOPE_PARAMS,
  TRIAL_STATUS_CHIPS,
} from '@sophocles/views/billing/components/trialUnitStatus';

/** odl_app resource path for the radios grid (list + grid-spec) — same descriptor RadioGrid uses. */
const RESOURCE_PATH = 'radios';

// Descriptor columns the radios search grid shows that this curated trial view
// doesn't: mac_address/placement_type are folded into the ICCID/MAC and
// Location columns below; ip_address/kore_status/location_timestamp are
// staff radio-detail fields with no place on this page.
const DEFAULT_HIDDEN_COLUMNS = {
  mac_address: false,
  ip_address: false,
  placement_type: false,
  kore_status: false,
  location_timestamp: false,
};

/** Trial-date cell value. A null date (unbacked trial radio, or an API that
 * hasn't landed the flat billing_trial_* fields yet) renders as a
 * placeholder rather than passing `undefined` into formatDate. */
function formatTrialDate(value, user) {
  return value ? formatDate(value, user) : '—';
}

function TrialRadioTable({ customerId, siteGroupId, statusFilter }) {
  const { t } = useTranslation();
  const { user } = useUser();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filterCustomerId, setFilterCustomerId] = useState(null);
  const [filterSiteGroupId, setFilterSiteGroupId] = useState(null);

  const effectiveCustomerId = customerId ?? filterCustomerId;
  const effectiveSiteGroupId = siteGroupId ?? filterSiteGroupId;

  const extraParams = useMemo(
    () => ({
      ...TRIAL_SCOPE_PARAMS,
      // A decommissioned radio is not a live billing concern — exclude it,
      // matching RadioGrid's default-active scope for the same `deleted`
      // field. staff_only on the radios spec, so a non-staff caller (this
      // page is staff-only anyway) would have the param silently ignored
      // rather than rejected.
      deleted: 'false',
      ...(effectiveCustomerId ? { customer_id: effectiveCustomerId } : {}),
      ...(effectiveSiteGroupId ? { site_group_id: effectiveSiteGroupId } : {}),
      ...(startDate ? { 'billing__trial_start_date.gte': format(startDate, DATE_PARAM_FORMAT) } : {}),
      ...(endDate ? { 'billing__trial_end_date.lte': format(endDate, DATE_PARAM_FORMAT) } : {}),
      // Applied last so a stat-card click wins over a stale manual date pick
      // on any overlapping key.
      ...(statusFilter ? trialStatusDateFilter(statusFilter) : {}),
    }),
    [effectiveCustomerId, effectiveSiteGroupId, startDate, endDate, statusFilter],
  );

  const fetchFn = useCallback(params => listRadios(params), []);

  const columnVisibility = useColumnVisibilityModel('trialUnitsList', DEFAULT_HIDDEN_COLUMNS);

  // Render override on the descriptor's own iccid column — combines ICCID/MAC
  // into one cell, same as the table's original hand-rolled column.
  const fieldOverrides = useMemo(
    () => ({
      iccid: {
        headerName: t('billing.iccidMac', 'ICCID/MAC'),
        renderCell: ({ row }) => row.iccid ?? row.mac_address ?? t('billing.unknown', 'Unknown'),
      },
    }),
    [t],
  );

  // Columns the descriptor doesn't model for this curated view: Customer (no
  // descriptor column at all), Location (supersedes the descriptor's site_name
  // column so it lands next to Customer in the same position the hand-rolled
  // table used), Trial Start/End (payload_only on the descriptor — never a
  // toggleable column), Status (client-derived), and the row's View action.
  const extraColumns = useMemo(
    () => [
      {
        field: 'customer',
        headerName: t('general.customer', 'Customer'),
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: ({ row }) => row.location?.customer?.name ?? '—',
      },
      {
        field: 'site_name',
        headerName: t('general.location', 'Location'),
        flex: 1,
        minWidth: 160,
        sortable: true,
        renderCell: ({ row }) =>
          row.location?.site?.name ??
          row.location?.site_group?.description ??
          row.location?.site_group?.name ??
          row.location?.inventory_location?.name ??
          '—',
      },
      {
        field: 'billing_trial_start_date',
        headerName: t('billing.trialStart', 'Trial Start'),
        width: 140,
        sortable: true,
        renderCell: ({ row }) => formatTrialDate(row.billing_trial_start_date, user),
      },
      {
        field: 'billing_trial_end_date',
        headerName: t('billing.trialEnd', 'Trial End'),
        width: 140,
        sortable: true,
        renderCell: ({ row }) => formatTrialDate(row.billing_trial_end_date, user),
      },
      {
        field: 'status',
        headerName: t('billing.status', 'Status'),
        width: 140,
        sortable: false,
        renderCell: ({ row }) => {
          const chip = TRIAL_STATUS_CHIPS[deriveTrialStatus(row.billing_trial_start_date, row.billing_trial_end_date)];
          return <StatusChip label={t(chip.labelKey, chip.fallback)} color={chip.color} />;
        },
      },
      {
        field: 'actions',
        headerName: t('billing.actions', 'Actions'),
        width: 120,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Button
            component={Link}
            to={`/support/radios/${row.id}`}
            onClick={e => e.stopPropagation()}
            variant="outlined"
            size="small"
          >
            {t('billing.view', 'View')}
          </Button>
        ),
      },
    ],
    [t, user],
  );

  const hasActiveFilters = Boolean(
    startDate || endDate || (customerId == null && filterCustomerId) || (siteGroupId == null && filterSiteGroupId),
  );

  const handleClearFilters = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    setFilterCustomerId(null);
    setFilterSiteGroupId(null);
  }, []);

  const handleCustomerChange = useCallback((event, newCustomerId) => {
    setFilterCustomerId(newCustomerId ?? null);
    // The previous site-group selection may not belong to the new customer.
    setFilterSiteGroupId(null);
  }, []);

  const handleSiteGroupChange = useCallback((event, newSiteGroupId) => {
    setFilterSiteGroupId(newSiteGroupId ?? null);
  }, []);

  const toolbarContent = useMemo(
    () => (
      <>
        {customerId == null && (
          <CustomerAutocomplete
            name="trialUnitsCustomer"
            value={filterCustomerId}
            onChange={handleCustomerChange}
            size="small"
            fullWidth
            sx={{ width: 240, flexShrink: 0 }}
          />
        )}
        {siteGroupId == null && (
          <Tooltip
            title={
              effectiveCustomerId == null
                ? t('billing.trialUnits.selectCustomerFirst', 'Select a customer to filter by group')
                : ''
            }
          >
            <span>
              <SiteGroupAutocomplete
                name="trialUnitsSiteGroup"
                customerId={effectiveCustomerId}
                value={filterSiteGroupId}
                onChange={handleSiteGroupChange}
                disabled={effectiveCustomerId == null}
                size="small"
                fullWidth
                sx={{ width: 220, flexShrink: 0 }}
              />
            </span>
          </Tooltip>
        )}
        <DatePicker
          label={t('billing.trialStart', 'Trial Start')}
          value={startDate}
          onChange={setStartDate}
          slotProps={{ textField: { size: 'small' } }}
        />
        <DatePicker
          label={t('billing.trialEnd', 'Trial End')}
          value={endDate}
          onChange={setEndDate}
          slotProps={{ textField: { size: 'small' } }}
        />
        {hasActiveFilters && <ClearAllChip onClick={handleClearFilters} />}
      </>
    ),
    [
      t,
      customerId,
      siteGroupId,
      filterCustomerId,
      filterSiteGroupId,
      effectiveCustomerId,
      handleCustomerChange,
      handleSiteGroupChange,
      startDate,
      endDate,
      hasActiveFilters,
      handleClearFilters,
    ],
  );

  return (
    <EntityGrid
      basePath={RESOURCE_PATH}
      fetchFn={fetchFn}
      fieldOverrides={fieldOverrides}
      extraParams={extraParams}
      options={{
        // Matches the table's original implicit default (useServerDataGrid's
        // no-defaultSort tiebreaker) — the descriptor's own default_order is -id.
        defaultSort: [{ field: 'id', sort: 'asc' }],
        persistKey: 'trialUnitsList',
        searchPlaceholder: t('billing.trialUnits.searchPlaceholder', 'Search ICCID or MAC'),
        toolbarContent,
        toolbarVariant: customerId != null || siteGroupId != null ? 'flat' : 'elevated',
        extraColumns,
        columnVisibility,
      }}
    />
  );
}

export default TrialRadioTable;
